import { useEffect } from 'react';
import { Shield, Eye, Database, Lock, UserCheck } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import PublicPageHeader from '@components/common/PublicPageHeader';
import { SEOHead, SEO_META_TAGS, useSEO } from '@/seo';

const Privacy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            title: "1. Information We Collect",
            content: "We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.",
            subsections: [
                { name: "Personal Information", items: ["Name and contact information (email address)", "Birth details (date, time, and place of birth)", "Account credentials and profile information", "Payment information (processed securely)"] },
                { name: "Usage Information", items: ["Chat messages and consultation history", "Service usage patterns and preferences", "Device information and IP address", "Log data and technical information"] }
            ]
        },
        {
            title: "2. How We Use Your Information",
            items: ["Provide personalized astrological consultations and insights", "Calculate accurate birth charts and planetary positions", "Process payments and Account Management", "Improve our services and develop new features", "Communicate with you about your account and our services", "Ensure the security and integrity of our platform"]
        },
        {
            title: "3. Information Sharing",
            content: "We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:",
            subsections: [
                { name: "Trusted Circumstances", items: ["Service Providers: Trusted partners who assist in operating our platform", "Legal Requirements: When required by law or to protect our rights", "Business Transfers: In the event of a merger or acquisition", "Consent: With your explicit consent"] }
            ]
        },
        {
            title: "4. Data Security",
            items: ["Encryption of sensitive data in transit and at rest", "Secure payment processing through reliable partners", "Regular security audits and updates", "Limited access to personal information on a need-to-know basis", "Secure server infrastructure and monitoring"]
        },
        { title: "5. Data Retention", content: "We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. You may request deletion of your account and associated data at any time, subject to legal retention requirements." },
        {
            title: "6. Your Rights",
            items: ["Access: Request access to your personal information", "Rectification: Request correction of inaccurate information", "Deletion: Request deletion of your personal information", "Portability: Request a copy of your data in a structured format", "Objection: Object to processing of your personal information"]
        }
    ];

    return (
        <div className="min-h-screen bg-cosmic-950 text-purple-50 font-sans selection:bg-purple-900 selection:text-white relative">
            <SEOHead {...useSEO(SEO_META_TAGS.privacy.English, 'English')} />
            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-32 md:pb-24 px-4 sm:px-6 font-sans">
                <div className="container mx-auto max-w-4xl">

                    <PublicPageHeader
                        title="Privacy Policy"
                        highlightedText="Policy"
                        description="Your privacy is important to us. This policy explains how we collect, use, and protect your personal information."
                        icon={Shield}
                    />

                    <div className="space-y-24">
                        {sections.map((section, i) => (
                            <section
                                key={i}
                                className="relative group"
                            >
                                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-white flex items-center gap-6 font-serif tracking-tight">
                                    <div className="w-16 h-16 rounded-[24px] bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shadow-[0_0_20px_rgba(147,51,234,0.1)] group-hover:bg-purple-500/20 transition-colors duration-500 flex-shrink-0">
                                        {i === 0 && <Database size={28} />}
                                        {i === 1 && <UserCheck size={28} />}
                                        {i === 2 && <Eye size={28} />}
                                        {i === 3 && <Shield size={28} />}
                                        {i === 4 && <Lock size={28} />}
                                        {i === 5 && <UserCheck size={28} />}
                                    </div>
                                    {section.title}
                                </h2>

                                {section.content && (
                                    <p className="text-purple-100/80 leading-relaxed font-sans mb-10 text-base lg:text-lg pl-1">
                                        {section.content}
                                    </p>
                                )}

                                {section.items && (
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {section.items.map((item, j) => (
                                            <li key={j} className="flex gap-4 text-purple-100/90 font-sans text-sm p-6 rounded-[32px] bg-white/[0.08] border border-white/20 hover:border-purple-400/50 transition-colors group/item">
                                                <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 flex-shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.5)] group-hover/item:scale-125 transition-transform" />
                                                <span className="leading-relaxed font-medium">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {section.subsections && section.subsections.map((sub, k) => (
                                    <div key={k} className="mt-12 pl-1">
                                        <h3 className="text-2xl font-bold text-white mb-8 font-serif">{sub.name}</h3>
                                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {sub.items.map((item, l) => (
                                                <li key={l} className="flex gap-4 text-purple-100/90 font-sans text-sm p-6 rounded-[32px] bg-white/[0.08] border border-white/20 hover:border-purple-400/50 transition-colors group/item">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 flex-shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.5)] group-hover/item:scale-125 transition-transform" />
                                                    <span className="leading-relaxed font-medium">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </section>
                        ))}
                    </div>

                    {/* Third party info */}
                    <div className="mt-32 p-12 border border-white/20 bg-white/[0.08] rounded-[48px] relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-12 opacity-5 -rotate-12 transition-transform duration-1000 group-hover:rotate-0">
                            <Shield size={160} className="text-purple-500" />
                        </div>
                        <h2 className="text-4xl font-black mb-12 text-white font-serif tracking-tight relative z-10">Our Infrastructure</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10 font-sans">
                            <div className="p-8 border border-white/20 rounded-[32px] bg-white/[0.08] group hover:bg-white/[0.12] transition-colors">
                                <h4 className="font-bold text-purple-300 mb-4 uppercase tracking-widest text-xs">Identity</h4>
                                <p className="leading-relaxed text-purple-100/80 text-sm">Secure authentication via Google OAuth for your account.</p>
                            </div>
                            <div className="p-8 border border-white/20 rounded-[32px] bg-white/[0.08] group hover:bg-white/[0.12] transition-colors">
                                <h4 className="font-bold text-purple-300 mb-4 uppercase tracking-widest text-xs">Financial</h4>
                                <p className="leading-relaxed text-purple-100/80 text-sm">Industry-standard secure processing for all transactions.</p>
                            </div>
                            <div className="p-8 border border-white/20 rounded-[32px] bg-white/[0.08] group hover:bg-white/[0.12] transition-colors">
                                <h4 className="font-bold text-purple-300 mb-4 uppercase tracking-widest text-xs">AI Services</h4>
                                <p className="leading-relaxed text-purple-100/80 text-sm">Powered by industry-leading AI providers for quality consultations.</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-24 p-16 border border-white/20 bg-white/[0.08] rounded-[56px] text-center relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                        <h2 className="text-4xl font-black mb-12 font-serif tracking-tight relative z-10">Contact Us</h2>
                        <div className="group relative z-10">
                            <h4 className="text-xs font-bold uppercase tracking-widest text-purple-400 mb-6 font-sans">For Privacy Inquiries</h4>
                            <a href="mailto:support@bhrigugpt.com" className="text-2xl md:text-4xl font-sans text-white hover:text-purple-300 transition-colors font-bold tracking-tight">support@bhrigugpt.com</a>
                        </div>
                        <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mx-auto mt-16 mb-8" />
                        <p className="text-xs text-purple-300/70 font-sans uppercase tracking-widest font-bold">Last Updated: August 13, 2025</p>
                    </div>

                </div>
            </main>

            <Footer />
            <MobileBottomNav />
        </div>
    );
};

export default Privacy;
